import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'wysko_active_generation';

interface PersistedGeneration {
  generationId: string;
  propertyId: string | null;
  imageId: string | null;
  startedAt: number;
}

interface GenerationState {
  isGenerating: boolean;
  propertyId: string | null;
  imageId: string | null;
  activeGenerationId: string | null;
}

export function useGenerationState() {
  const [state, setState] = useState<GenerationState>(() => {
    // Restore from localStorage on mount
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: PersistedGeneration = JSON.parse(stored);
        // Only restore if less than 15 minutes old (after that, cron cleanup handles it)
        if (Date.now() - parsed.startedAt < 15 * 60 * 1000) {
          return {
            isGenerating: true,
            propertyId: parsed.propertyId,
            imageId: parsed.imageId,
            activeGenerationId: parsed.generationId,
          };
        }
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Ignore parse errors
    }
    return {
      isGenerating: false,
      propertyId: null,
      imageId: null,
      activeGenerationId: null,
    };
  });

  const startGenerating = useCallback((propertyId: string | null, imageId: string | null) => {
    setState(prev => ({ ...prev, isGenerating: true, propertyId, imageId }));
  }, []);

  const stopGenerating = useCallback(() => {
    setState(prev => ({
      ...prev,
      isGenerating: false,
      propertyId: null,
      imageId: null,
    }));
  }, []);

  const setActiveGenerationId = useCallback((generationId: string | null) => {
    setState(prev => {
      const newState = { ...prev, activeGenerationId: generationId };
      if (generationId) {
        newState.isGenerating = true;
      }
      return newState;
    });

    // Persist to localStorage
    if (generationId) {
      try {
        const persisted: PersistedGeneration = {
          generationId,
          propertyId: state.propertyId,
          imageId: state.imageId,
          startedAt: Date.now(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
      } catch {
        // Ignore storage errors
      }
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [state.propertyId, state.imageId]);

  // Clear localStorage when generation finishes
  useEffect(() => {
    if (!state.activeGenerationId) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [state.activeGenerationId]);

  return {
    isGenerating: state.isGenerating,
    generatingPropertyId: state.propertyId,
    generatingImageId: state.imageId,
    activeGenerationId: state.activeGenerationId,
    startGenerating,
    stopGenerating,
    setActiveGenerationId,
  };
}
